import { useEffect, useRef, useState } from 'react';
import { Mic } from 'lucide-react';
import { buildScopeIntake, SCOPE_CONTEXT_LABELS, type ScopeContext, type ScopeInputSource } from '@/domain/scopeIntake';

type ScopeRecognition = {
  lang: string;
  interimResults: boolean;
  maxAlternatives: number;
  onresult: ((event: { results: ArrayLike<ArrayLike<{ transcript: string }>> }) => void) | null;
  onerror: (() => void) | null;
  onend: (() => void) | null;
  start: () => void;
  abort: () => void;
};
type ScopeSpeechWindow = Window & {
  SpeechRecognition?: new () => ScopeRecognition;
  webkitSpeechRecognition?: new () => ScopeRecognition;
};

// Context-only draft: deliberately has no estimate/store/persistence callbacks.
export function ScopeIntakeCard() {
  const [input, setInput] = useState<{ rawText: string; source: ScopeInputSource }>({ rawText: '', source: 'text' });
  const [chosenContext, setChosenContext] = useState<ScopeContext | ''>('');
  const [isListening, setIsListening] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const recognitionRef = useRef<ScopeRecognition | null>(null);
  const draft = buildScopeIntake(input.rawText, input.source);
  const context = chosenContext || (draft.needsContextConfirmation ? '' : draft.context);

  function cancelDictation() {
    const recognition = recognitionRef.current;
    recognitionRef.current = null;
    if (recognition) {
      recognition.onresult = null;
      recognition.onerror = null;
      recognition.onend = null;
      recognition.abort();
    }
  }

  useEffect(() => cancelDictation, []);

  function updateRawText(rawText: string, source: ScopeInputSource) {
    setInput({ rawText, source });
    setChosenContext('');
    setError(null);
  }

  function dictate() {
    if (recognitionRef.current) return;
    const speechWindow = window as ScopeSpeechWindow;
    const Recognition = speechWindow.SpeechRecognition ?? speechWindow.webkitSpeechRecognition;
    if (!Recognition) {
      setError('Dyktowanie niedostępne w tej przeglądarce. Wpisz zakres ręcznie.');
      return;
    }
    setError(null);
    try {
      const recognition = new Recognition();
      recognitionRef.current = recognition;
      recognition.lang = 'pl-PL';
      recognition.interimResults = false;
      recognition.maxAlternatives = 1;
      recognition.onresult = (event) => {
        const transcript = event.results[0]?.[0]?.transcript;
        if (transcript?.trim()) updateRawText(transcript, 'voice');
      };
      recognition.onerror = () => {
        cancelDictation();
        setIsListening(false);
        setError('Nie udało się rozpoznać głosu. Sprawdź mikrofon lub wpisz zakres ręcznie.');
      };
      recognition.onend = () => {
        recognitionRef.current = null;
        setIsListening(false);
      };
      setIsListening(true);
      recognition.start();
    } catch {
      cancelDictation();
      setIsListening(false);
      setError('Nie udało się uruchomić mikrofonu. Wpisz zakres ręcznie.');
    }
  }

  return (
    <section aria-labelledby="scope-intake-heading" className="mb-5 rounded-[28px] border border-[#2a2f38] bg-[#15191f] p-5">
      <h2 id="scope-intake-heading" className="text-2xl font-black text-white">Co robisz?</h2>
      <p id="scope-intake-hint" className="mt-2 text-sm text-slate-300">
        Opisz miejsce i planowane prace. To szkic do zatwierdzenia przez Ciebie. Opis i kontekst nie zmieniają ceny ani pozycji wyceny.
      </p>
      <label htmlFor="scope-intake-text" className="mt-4 block text-sm font-semibold text-slate-300">Opisz zakres prac</label>
      <textarea
        id="scope-intake-text"
        aria-describedby="scope-intake-hint"
        value={input.rawText}
        onChange={(event) => {
          cancelDictation();
          setIsListening(false);
          updateRawText(event.target.value, 'text');
        }}
        rows={3}
        placeholder="np. Odświeżenie pokoju, ściany i sufit"
        className="mt-2 w-full min-w-0 rounded-xl border border-[#2a2f38] bg-[#0f1217] px-3 py-3 text-white"
      />
      <button type="button" onClick={isListening ? () => { cancelDictation(); setIsListening(false); } : dictate} className="mt-3 flex min-h-14 items-center justify-center gap-2 rounded-xl border border-cyan-400/40 px-4 font-bold text-cyan-100">
        <Mic className="h-4 w-4" />
        {isListening ? 'Zakończ dyktowanie' : 'Podyktuj zakres'}
      </button>
      <p className="mt-2 text-xs text-slate-400">Dyktowanie zastępuje opis. Sprawdź rozpoznany tekst; dostępność zależy od przeglądarki i może wymagać internetu.</p>
      {isListening && <p role="status" className="mt-2 text-sm text-cyan-100">Słucham — opisz zakres po polsku.</p>}
      {error && <p role="alert" className="mt-2 text-sm text-amber-100">{error}</p>}
      {draft.rawText && (
        <div className="mt-4 rounded-xl border border-[#2a2f38] bg-[#0f1217] p-4">
          <p className="text-sm text-slate-300">Rozpoznany kontekst: <strong className="text-white">{draft.contextLabel}</strong></p>
          <p className="mt-3 text-sm font-semibold text-slate-300">Kontekst szkicu</p>
          {draft.needsContextConfirmation && (
            <p className="mt-2 text-sm text-amber-100">
              {draft.context === 'paving' ? (
                <>
                  Doprecyzuj rodzaj realizacji i wybierz właściwy kontekst szkicu.
                  <br />
                  Sam bruk nie oznacza podjazdu.
                </>
              ) : (
                <>
                  Nie udało się jednoznacznie rozpoznać rodzaju realizacji.
                  <br />
                  Wybierz właściwy kontekst szkicu.
                </>
              )}
            </p>
          )}
          <label htmlFor="scope-intake-context" className="mt-2 block text-sm font-semibold text-slate-300">Wybierz kontekst szkicu</label>
          <select id="scope-intake-context" value={context} onChange={(event) => { setChosenContext(event.target.value as ScopeContext); }} className="mt-2 min-h-14 w-full min-w-0 rounded-xl border border-[#2a2f38] bg-[#15191f] px-3 text-white">
            <option value="">Wybierz rodzaj realizacji</option>
            {Object.entries(SCOPE_CONTEXT_LABELS).filter(([value]) => value !== 'paving').map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
          <p role="status" className="mt-2 text-sm text-slate-300">{context ? `Wybrany kontekst: ${SCOPE_CONTEXT_LABELS[context]}.` : 'Wybierz kontekst szkicu.'}</p>
          <p className="mt-2 text-xs text-slate-400">To nie zmienia zakresu ani ceny. Zakres do wyceny wybierz poniżej.</p>
        </div>
      )}
    </section>
  );
}
